import React from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { useTheme } from '@mui/styles';
import { getNameSegmentType, ISaleOrderListItem } from '../../types';
import { formatDateTimeToShowUser, formatDateToShowUser } from '../../../common/helpers';
import { serviceTypeToEmoji } from '../../constants';
import { getSaleOrderInvoicingCellLabel } from '../SaleOrderInvoicingStatusCell';

export function joinTextAndCapitalize(text: string | any[] | undefined): string {
	if (!text) {
		return '';
	}

	let fullText = '';
	if (Array.isArray(text)) {
		fullText = text.join(', ');
	} else {
		fullText = text;
	}
	return fullText.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function processText(text: string | any[] | undefined, limit: number): string {
	const capitalized = joinTextAndCapitalize(text);
	if (capitalized.length > limit) {
		return capitalized.substring(0, limit) + '...';
	}
	return capitalized;
}

export const transformCustomersCompleteName = (customersCompleteName?: string[]): string =>
	processText(customersCompleteName, 30) || '🧐 Cliente NO definido';

export const calculateChargedPercent = (row: ISaleOrderListItem): number => {
	return (row.chargedAmount ?? 0) + (row.pendingPayAmount ?? 0) > 0
		? ((row.chargedAmount ?? 0) / ((row.chargedAmount ?? 0) + (row.pendingPayAmount ?? 0))) * 100
		: 0;
};

/** Puede superar 100 cuando la orden está sobre-facturada. */
export const calculateInvoicedPercent = (row: ISaleOrderListItem): number => {
	if (!row.saleAmount || row.saleAmount <= 0) return 0;
	return ((row.invoicedAmount ?? 0) / row.saleAmount) * 100;
};

export const calculateDaysToTravel = (travelDate?: string | number | Date): string | number => {
	if (!travelDate) return '-';
	const today = new Date();
	const departureDate = new Date(travelDate);
	if (isNaN(departureDate.getTime())) {
		return '-';
	}
	const diffTime = departureDate.getTime() - today.getTime();
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	return diffDays > 0 ? diffDays : 0;
};

export const transformValue = (
	key: string,
	value: string | number | boolean | undefined,
	row: ISaleOrderListItem,
): string | number | boolean | undefined => {
	switch (key) {
		case 'created':
			return formatDateTimeToShowUser(row.created).replace(/\.\d+$/, '');
		case 'mainPassengerCompleteName':
			return joinTextAndCapitalize(row.mainPassengerCompleteName) || 'Pax principal NO definido';
		case 'customersCompleteName':
			return joinTextAndCapitalize(row.customersCompleteName) || 'Cliente NO definido';
		case 'isBitrixDeal':
			return row.isBitrixDeal ? 'Si' : 'No';
		case 'quotationId':
			return row.quotationId ? row.quotationId : '-';
		case 'salesAmount':
			return row.saleAmount ? row.saleAmount : '-';
		case 'totalCost':
			return row.totalCost ? row.totalCost : '-';
		case 'chargedAmount':
			return row.chargedAmount ? row.chargedAmount : '-';
		case 'chargedPercent':
			return Math.round(calculateChargedPercent(row));
		case 'invoicedPercent':
			return Math.round(calculateInvoicedPercent(row));
		case 'pendingPayAmount':
			return row.pendingPayAmount ? row.pendingPayAmount : '-';
		case 'grossProfit':
			return row.grossProfit !== undefined ? row.grossProfit : '-';
		case 'destinations':
			return row.destinations ? row.destinations : '-';
		case 'travelDepartureDate':
			return row.travelDepartureDate ? formatDateToShowUser(row.travelDepartureDate) : '-';

		case 'daysToTravel':
			return calculateDaysToTravel(row.travelDepartureDate);
		case 'travelReturnDate':
			return row.travelReturnDate ? formatDateToShowUser(row.travelReturnDate) : '-';
		case 'isReturnDateReached':
			return row.isReturnDateReached ? 'Sí' : 'No';
		case 'lastSuccessSyncAt':
			return row.lastSuccessSyncAt ? formatDateToLocal(row.lastSuccessSyncAt) : '-';
		case 'updated':
			return row.updated ? formatDateToLocal(row.updated) : '-';
		case 'segmentType':
			return getNameSegmentType(row.segmentType);
		case 'channel':
			return row.channel?.name || '-';
		case 'clientReference':
			return row.clientReference || '-';

		case 'invoicingStatus':
			return getSaleOrderInvoicingCellLabel(row);
		case 'servicesTypes':
			if (row.servicesTypes && row.servicesTypes.length > 0) {
				return row.servicesTypes
					.map((type: string) => {
						const service = serviceTypeToEmoji[type];
						return service.name || type;
					})
					.join(', ');
			} else return '-';

		default:
			return value;
	}
};

// Componente para transformar grossProfit usando hooks correctamente
// eslint-disable-next-line react/prop-types
export const TransformGrossProfit: React.FC<{ grossProfit?: number }> = ({ grossProfit }) => {
	const theme = useTheme();

	if (grossProfit === undefined) {
		return <>{'-'}</>;
	}

	const getGrossProfitStyles = (grossProfit: number) => {
		if (grossProfit > 0) {
			return {
				icon: { fontSize: 20, color: theme.palette.success.main },
				text: { color: theme.palette.success.main },
			};
		} else if (grossProfit < 0) {
			return {
				icon: { fontSize: 20, color: theme.palette.error.dark },
				text: { color: theme.palette.error.dark },
			};
		} else {
			return {
				icon: { fontSize: 20, color: theme.palette.info.main },
				text: { color: theme.palette.info.main },
			};
		}
	};

	const styles = getGrossProfitStyles(grossProfit);

	return (
		<>
			<span style={styles.text}>{grossProfit}</span>
			<span style={styles.icon}>
				{grossProfit > 0 ? <TrendingUpIcon /> : grossProfit < 0 ? <TrendingDownIcon /> : <TrendingFlatIcon />}
			</span>
		</>
	);
};

/**
 * Margen bruto porcentual: margen sobre venta.
 *
 * Es un cociente, así que no depende de la moneda de la orden — se puede
 * mostrar al lado del margen absoluto sin convertir nada. Sin venta no hay
 * porcentaje que calcular: se muestra un guion en vez de inventar un 0%.
 */
interface GrossProfitPercentProps {
	grossProfit?: number;
	saleAmount?: number;
}

// eslint-disable-next-line react/prop-types
export const TransformGrossProfitPercent: React.FC<GrossProfitPercentProps> = ({ grossProfit, saleAmount }) => {
	const theme = useTheme();

	if (grossProfit === undefined || !saleAmount) {
		return <>{'-'}</>;
	}

	const percent = (grossProfit / saleAmount) * 100;
	const color =
		percent > 0 ? theme.palette.success.main : percent < 0 ? theme.palette.error.dark : theme.palette.info.main;

	return <span style={{ color }}>{`${percent.toFixed(1)}%`}</span>;
};

// Función de compatibilidad para mantener la API existente (pero ahora usa el componente)
export const transformGrossProfit = (grossProfit?: number): JSX.Element | string => {
	// Esta función ahora retorna el componente, pero se debe usar directamente el componente
	// en el JSX para evitar problemas con hooks
	return <TransformGrossProfit grossProfit={grossProfit} />;
};

export const formatDateToLocal = (input: string): string => {
	const date = new Date(input);

	// Formatear la fecha en DD/MM/YYYY
	const day = date.getDate().toString().padStart(2, '0');
	const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Los meses son 0-indexados
	const year = date.getFullYear();

	// Formatear la hora en formato local
	const time = date.toLocaleTimeString();

	return `${day}/${month}/${year} ${time}`;
};
